import { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Users, IndianRupee, X, CheckCircle2 } from "lucide-react";

export default function PortalFacilities() {
  const [resources, setResources] = useState([]);
  const [mine, setMine] = useState([]);
  const [openBook, setOpenBook] = useState(null); // resource

  const load = async () => {
    const [r, b] = await Promise.all([api.get("/portal/facilities/resources"), api.get("/portal/facilities/bookings")]);
    setResources(r.data); setMine(b.data);
  };
  useEffect(() => { load().catch((e) => toast.error(apiErr(e))); }, []);

  const cancel = async (id) => {
    try { await api.post(`/portal/facilities/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <PortalLayout subtitle="Community" title="Book a Facility">
      <section className="mb-10">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Available facilities</div>
        {resources.length === 0 && <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-500">No bookable facilities yet. Your committee will add them soon.</div>}
        <div className="grid md:grid-cols-2 gap-4">
          {resources.map((r) => (
            <div key={r.id} className="bg-white border border-stone-200 rounded-2xl p-6" data-testid={`res-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-terracotta font-semibold">{r.booking_type === "time_slot" ? `${r.slot_duration_hours}h slots` : "Full day"}</div>
                  <div className="editorial text-2xl mt-1">{r.name}</div>
                  <div className="text-sm text-stone-500 mt-1">{r.description}</div>
                </div>
                {r.fee > 0 && <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Fee</div>
                  <div className="editorial text-xl">₹ {r.fee.toLocaleString()}</div>
                </div>}
              </div>
              <div className="flex flex-wrap gap-3 mt-4 text-xs text-stone-500">
                {r.capacity > 0 && <span className="inline-flex items-center gap-1"><Users size={12}/> Up to {r.capacity}</span>}
                <span className="inline-flex items-center gap-1"><CalendarDays size={12}/> Up to {r.advance_days} days ahead</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${r.approval_mode === "auto_confirm" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {r.approval_mode === "auto_confirm" ? "Auto-confirmed" : "Committee approval"}
                </span>
              </div>
              <button onClick={() => setOpenBook(r)} data-testid={`book-${r.id}`}
                className="mt-5 w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-5 py-2.5 text-sm font-medium">
                Book this facility
              </button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">My bookings</div>
        <div className="space-y-3">
          {mine.length === 0 && <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center text-stone-500">You have no bookings yet.</div>}
          {mine.map((b) => (
            <div key={b.id} className="bg-white border border-stone-200 rounded-2xl p-5 flex items-center gap-4" data-testid={`mybook-${b.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="editorial text-lg">{b.resource_name}</div>
                  <BookingStatus s={b.status}/>
                </div>
                <div className="text-sm text-stone-500 mt-1">
                  {b.date}{b.slot_start ? ` · ${b.slot_start}–${b.slot_end}` : " · Full day"}
                  {b.purpose && ` · ${b.purpose}`}
                </div>
                {b.decision_reason && <div className="text-xs text-stone-500 mt-1 italic">"{b.decision_reason}"</div>}
              </div>
              {b.fee > 0 && <div className="text-right"><div className="text-xs text-stone-500">Fee</div><div className="editorial">₹ {b.fee}</div></div>}
              {["Pending", "Confirmed"].includes(b.status) && (
                <button onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}
                  className="rounded-full border border-stone-300 hover:bg-sand-100 text-stone-700 px-3 py-1.5 text-xs">Cancel</button>
              )}
            </div>
          ))}
        </div>
      </section>

      {openBook && <BookingModal resource={openBook} onClose={() => setOpenBook(null)} onDone={() => { setOpenBook(null); load(); }}/>}
    </PortalLayout>
  );
}

function BookingStatus({ s }) {
  const m = { Pending: "bg-amber-100 text-amber-800", Confirmed: "bg-emerald-100 text-emerald-800", Rejected: "bg-red-100 text-red-800", Cancelled: "bg-stone-100 text-stone-700" };
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${m[s] || "bg-stone-100"}`}>{s}</span>;
}

function BookingModal({ resource, onClose, onDone }) {
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [avail, setAvail] = useState(null);
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [purpose, setPurpose] = useState("");
  const [headcount, setHeadcount] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAvail = async (d) => {
    if (!d) return;
    try {
      const { data } = await api.get(`/portal/facilities/resources/${resource.id}/availability`, { params: { date: d } });
      setAvail(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { loadAvail(date); /* eslint-disable-next-line */ }, [date]);

  const slots = useMemo(() => {
    if (resource.booking_type !== "time_slot") return [];
    const dur = Number(resource.slot_duration_hours || 2);
    const [oh, om] = (resource.open_time || "06:00").split(":").map(Number);
    const [ch, cm] = (resource.close_time || "22:00").split(":").map(Number);
    const start = oh * 60 + om;
    const end = ch * 60 + cm;
    const step = dur * 60;
    const list = [];
    for (let m = start; m + step <= end; m += step) {
      const s = `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
      const e2 = `${String(Math.floor((m+step)/60)).padStart(2,"0")}:${String((m+step)%60).padStart(2,"0")}`;
      list.push({ s, e: e2 });
    }
    return list;
  }, [resource]);

  const isSlotTaken = (s, e) => {
    if (!avail) return false;
    if (avail.blocked_out) return true;
    return (avail.taken || []).some((t) => {
      if (!t.slot_start || !t.slot_end) return true;
      return !(e <= t.slot_start || s >= t.slot_end);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { resource_id: resource.id, date, purpose, headcount: Number(headcount || 0) };
      if (resource.booking_type === "time_slot") { body.slot_start = slotStart; body.slot_end = slotEnd; }
      await api.post("/portal/facilities/bookings", body);
      toast.success(resource.approval_mode === "auto_confirm" ? "Booking confirmed" : "Booking submitted for committee approval");
      onDone();
    } catch (e2) { toast.error(apiErr(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl border border-stone-200 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-terracotta font-semibold">Book</div>
            <div className="editorial text-lg mt-0.5">{resource.name}</div>
          </div>
          <button onClick={onClose} className="p-2 text-stone-500 hover:text-ink"><X size={20}/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 overflow-y-auto">
          {avail?.blocked_out && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3">This day is blocked out{avail.blackout_reason ? `: ${avail.blackout_reason}` : ""}. Pick another date.</div>}
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={new Date().toISOString().slice(0,10)} required
              data-testid="book-date"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
          </div>
          {resource.booking_type === "time_slot" && (
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Slot</label>
              <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                {slots.map((sl) => {
                  const taken = isSlotTaken(sl.s, sl.e);
                  const active = slotStart === sl.s && slotEnd === sl.e;
                  return (
                    <button key={sl.s} type="button" disabled={taken}
                      onClick={() => { setSlotStart(sl.s); setSlotEnd(sl.e); }}
                      data-testid={`slot-${sl.s}`}
                      className={`rounded-xl border px-3 py-2 text-sm transition-colors ${taken ? "bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed line-through" : active ? "bg-terracotta text-white border-terracotta" : "bg-white border-stone-300 hover:border-terracotta"}`}>
                      {sl.s} – {sl.e}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Purpose</label>
            <input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Birthday party, cultural meet, etc."
              data-testid="book-purpose"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Expected headcount</label>
            <input type="number" value={headcount} onChange={(e) => setHeadcount(e.target.value)}
              data-testid="book-headcount"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
          </div>
          {resource.fee > 0 && (
            <div className="rounded-xl bg-sand-100 border border-stone-200 p-3 text-sm text-stone-700 flex items-center gap-2">
              <IndianRupee size={14}/> A booking fee of ₹ {resource.fee.toLocaleString()} will be invoiced upon confirmation.
            </div>
          )}
          {resource.cancellation_policy && (
            <div className="text-xs text-stone-500 italic">Cancellation: {resource.cancellation_policy}</div>
          )}
          <button type="submit" disabled={busy || avail?.blocked_out || (resource.booking_type === "time_slot" && !slotStart)}
            data-testid="book-submit"
            className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2">
            <CheckCircle2 size={16}/> {resource.approval_mode === "auto_confirm" ? "Confirm booking" : "Submit for approval"}
          </button>
        </form>
      </div>
    </div>
  );
}
